import { useEffect, useState } from "react";
import { useServices } from "../app/services.ts";
import { Icon } from "../ui/Icon.tsx";
import { MapView } from "./MapView.tsx";
import styles from "./Location.module.css";

const DURATIONS: [number | null, string][] = [
  [900, "15 Minuten"],
  [3600, "1 Stunde"],
  [28800, "8 Stunden"],
  [null, "Bis ich stoppe"],
];

/** "Standort" from the composer: the current position, a pin on the map, or live sharing. */
export function LocationPicker({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const services = useServices();
  const [here, setHere] = useState<{ lat: number; lon: number; acc: number } | null>(null);
  const [pin, setPin] = useState<{ lat: number; lon: number } | null>(null);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(false);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (p) => setHere({ lat: p.coords.latitude, lon: p.coords.longitude, acc: Math.round(p.coords.accuracy) }),
      (e) => setError(e.code === 1 ? "Der Browser darf den Standort nicht lesen. Erlaube ihn über das Symbol in der Adressleiste, oder setz einen Punkt auf der Karte." : "Der Standort ließ sich nicht bestimmen. Setz einen Punkt auf der Karte."),
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  }, []);

  async function send(place: { lat: number; lon: number; acc?: number; place: boolean }) {
    setBusy(true);
    await services.sendPlace(conversationId, { ...place, live: false }).then(onClose, () => setError("Der Standort ging nicht raus."));
    setBusy(false);
  }

  async function share(duration: number | null) {
    setBusy(true);
    await services.startLiveLocation(conversationId, duration).then(onClose, () => setError("Live-Standort ließ sich nicht starten."));
    setBusy(false);
  }

  const pins = [...(here ? [{ id: "here", lat: here.lat, lon: here.lon, color: "#1971c2", label: "Du bist hier" }] : []), ...(pin ? [{ id: "pin", lat: pin.lat, lon: pin.lon, color: "#e03131", label: "Gewählter Ort" }] : [])];
  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.dialog} role="dialog" aria-label="Standort senden" onClick={(e) => e.stopPropagation()}>
        <header>
          <b>Standort</b>
          <button onClick={onClose} aria-label="Schließen">
            <Icon name="close" />
          </button>
        </header>
        <div className={styles.map}>{(here || error) && <MapView pins={pins} center={here ? [here.lat, here.lon] : undefined} zoom={here ? 15 : 5} onPick={(lat, lon) => setPin({ lat, lon })} />}</div>
        <p className={styles.note}>{error ?? (here ? `Genauigkeit etwa ${here.acc} m · auf die Karte klicken, um einen anderen Ort zu wählen` : "Standort wird bestimmt …")}</p>
        {!live ? (
          <div className={styles.actions}>
            {pin && (
              <button className={styles.primary} disabled={busy} onClick={() => void send({ ...pin, place: true })}>
                Diesen Ort senden
              </button>
            )}
            {here && (
              <button className={pin ? styles.outlined : styles.primary} disabled={busy} onClick={() => void send({ ...here, place: false })}>
                Aktuellen Standort senden
              </button>
            )}
            <button className={styles.outlined} disabled={busy} onClick={() => setLive(true)}>
              Live-Standort teilen
            </button>
          </div>
        ) : (
          <div className={styles.actions}>
            <span className={styles.note}>Wie lange? Im Browser nur, solange Voxie offen ist.</span>
            {DURATIONS.map(([d, text]) => (
              <button key={text} className={styles.outlined} disabled={busy} onClick={() => void share(d)}>
                {text}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
